import React, { useEffect, useState, useCallback, useRef } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import ApiService from '../../services/apiService';
import Loader from '../../elements/Loader';

/* ─── Campaign types ──────────────────────────────────────────────────────── */

const CAMPAIGN_TYPES = [
  { value: 'festival_campaign', label: 'Festival Campaign', icon: 'fa-star',         color: '#f4a02b' },
  { value: 'discount_campaign', label: 'Discount Campaign', icon: 'fa-tag',          color: '#e83e8c' },
  { value: 'birthday_campaign', label: 'Birthday Campaign', icon: 'fa-birthday-cake',color: '#6c5ffc' },
  { value: 'welcome_campaign',  label: 'Welcome Campaign',  icon: 'fa-handshake-o',  color: '#fd7e14' },
  { value: 'custom_campaign',   label: 'Custom Campaign',   icon: 'fa-bullhorn',     color: '#6c757d' },
];

const TYPE_MAP = Object.fromEntries(CAMPAIGN_TYPES.map(t => [t.value, t]));

/* ─── Predefined campaign name options (non-festival types) ───────────────── */
const STATIC_NAME_OPTIONS = {
  discount_campaign: [
    'Navratri Special Discount',
    'Diwali Sale Offer',
    'New Year Discount',
    'Monsoon Special Offer',
    'Weekend Flash Sale',
    'Festive Season Discount',
    'Summer Puja Offer',
    'Early Bird Discount',
  ],
  birthday_campaign: [
    'Birthday Special Blessing',
    'Birthday Puja Offer',
    'Happy Birthday Greeting',
    'Birthday Discount Offer',
  ],
  welcome_campaign: [
    'Welcome to Teertham',
    'New User Welcome Offer',
    'Welcome Blessing Message',
    'Get Started with Puja',
  ],
  custom_campaign: [
    'Teertham Special Announcement',
    'Auspicious Day Reminder',
    'Puja Season Kickoff',
    'Divine Blessings Campaign',
  ],
};

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

const STATUS_META = {
  active:    { cls: 'badge-success', label: 'Active' },
  triggered: { cls: 'badge-primary', label: 'Triggered' },
  paused:    { cls: 'badge-warning', label: 'Paused' },
};

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}

function daysFromNow(dateStr) {
  const diff = Math.round(
    (new Date(dateStr) - new Date(new Date().toISOString().split('T')[0])) / 86400000
  );
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff < 0) return `${Math.abs(diff)}d ago`;
  return `${diff}d away`;
}

const DAILY_LIMIT_PRESETS = [
  { value: '250',    label: '250 / day' },
  { value: '500',    label: '500 / day' },
  { value: '750',    label: '750 / day' },
  { value: 'custom', label: 'Custom'    },
];

const EMPTY_FORM = {
  actionType:       'festival_campaign',
  campaignName:     '',
  selectedPuja:     null,
  selectedSegment:  null,
  scheduledDate:    '',
  scheduledTime:    '09:00',
  notes:            '',
  dailyLimit:       '250',
  dailyLimitCustom: '',
};

/* ─── Component ───────────────────────────────────────────────────────────── */

export default function WhatsAppMarketing() {
  const [campaigns,      setCampaigns]      = useState([]);
  const [calEvents,      setCalEvents]      = useState([]);
  const [pujaOptions,    setPujaOptions]    = useState([]);
  const [segmentOptions, setSegmentOptions] = useState([]);

  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingCal,       setLoadingCal]       = useState(false);
  const [loadingPujas,     setLoadingPujas]     = useState(false);
  const [saving,           setSaving]           = useState(false);
  const [triggeringId,     setTriggeringId]     = useState(null);
  const [showForm,         setShowForm]         = useState(false);
  const [filterType,       setFilterType]       = useState('all');
  const [editingId,        setEditingId]        = useState(null);
  const [confirmCampaign,  setConfirmCampaign]  = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const formRef = useRef(null);

  const activeType = TYPE_MAP[form.actionType] || CAMPAIGN_TYPES[0];

  // Campaign name options: festivals from calendar, or predefined list for other types
  const campaignNameOptions = form.actionType === 'festival_campaign'
    ? calEvents.map(e => ({ value: e.eventName, label: e.eventName }))
    : (STATIC_NAME_OPTIONS[form.actionType] || []).map(n => ({ value: n, label: n }));

  /* ── fetchers ── */
  const fetchCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const res = await ApiService.get('festival-campaign');
      setCampaigns(res?.data || []);
    } catch (err) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoadingCampaigns(false);
    }
  }, []);

  const fetchCalendar = useCallback(async () => {
    setLoadingCal(true);
    try {
      const res = await ApiService.get('festival-campaign/upcoming-calendar?days=60');
      setCalEvents(res?.data || []);
    } catch (err) {
      toast.error('Failed to load upcoming festivals');
    } finally {
      setLoadingCal(false);
    }
  }, []);

  const fetchPujas = useCallback(async () => {
    setLoadingPujas(true);
    try {
      const res = await ApiService.get('service/list');
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setPujaOptions(list.map(s => ({ value: s.id, label: s.title || s.name || `Puja #${s.id}` })));
    } catch (err) {
      toast.error('Could not load puja list');
    } finally {
      setLoadingPujas(false);
    }
  }, []);

  const fetchSegments = useCallback(async () => {
    try {
      const res = await ApiService.get('promotion/segment');
      const list = Array.isArray(res?.data) ? res.data : [];
      setSegmentOptions(
        list.map(s => ({ value: s.id, label: `${s.name}${s.userCount != null ? ` (${s.userCount} users)` : ''}` }))
      );
    } catch (err) {
      // non-critical
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    fetchCalendar();
    fetchPujas();
    fetchSegments();
  }, [fetchCampaigns, fetchCalendar, fetchPujas, fetchSegments]);

  const prefillFromEvent = (evt) => {
    setForm(prev => ({
      ...prev,
      actionType:   'festival_campaign',
      campaignName: evt.eventName || '',
      scheduledDate: evt.eventDate || '',
      selectedPuja: evt.pujaId
        ? (pujaOptions.find(o => o.value === evt.pujaId) || { value: evt.pujaId, label: evt.recommendedPujaName || `Puja #${evt.pujaId}` })
        : null,
    }));
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleEdit = (c) => {
    const presetValues = ['250', '500', '750'];
    const limitStr = c.dailyMessageLimit ? String(c.dailyMessageLimit) : '250';
    const dailyLimit = presetValues.includes(limitStr) ? limitStr : 'custom';
    setForm({
      actionType:       c.actionType || 'festival_campaign',
      campaignName:     c.festivalName || '',
      selectedPuja:     c.pujaId ? { value: c.pujaId, label: c.pujaName || `Puja #${c.pujaId}` } : null,
      selectedSegment:  c.segment ? { value: c.segment.id, label: c.segment.name } : null,
      scheduledDate:    c.scheduledDate || '',
      scheduledTime:    c.scheduledTime || '09:00',
      notes:            c.notes || '',
      dailyLimit,
      dailyLimitCustom: dailyLimit === 'custom' ? limitStr : '',
    });
    setEditingId(c.id);
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  /* ── submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const { actionType, campaignName, selectedPuja, selectedSegment, scheduledDate, scheduledTime, dailyLimit, dailyLimitCustom } = form;

    if (!campaignName.trim())      { toast.error('Campaign name is required'); return; }
    if (!selectedSegment?.value)   { toast.error('Please select a target segment'); return; }
    if (!scheduledDate)            { toast.error('Select a date'); return; }
    if (!scheduledTime)            { toast.error('Select a time'); return; }
    if (dailyLimit === 'custom' && !dailyLimitCustom) {
      toast.error('Enter a custom daily message limit');
      return;
    }

    const dailyMessageLimit = dailyLimit === 'custom'
      ? (parseInt(dailyLimitCustom) || null)
      : parseInt(dailyLimit) || null;

    const payload = {
      actionType,
      festivalName:      campaignName.trim(),
      pujaId:            selectedPuja?.value || null,
      pujaName:          selectedPuja?.label || campaignName.trim(),
      scheduledDate,
      scheduledTime,
      notes:             form.notes.trim() || undefined,
      segmentId:         selectedSegment.value,
      dailyMessageLimit,
    };

    setSaving(true);
    try {
      if (editingId) {
        await ApiService.put(`festival-campaign/${editingId}`, payload);
        toast.success('Campaign updated');
      } else {
        await ApiService.post('festival-campaign', payload);
        toast.success('Campaign saved');
      }
      setForm(EMPTY_FORM);
      setShowForm(false);
      setEditingId(null);
      fetchCampaigns();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await ApiService.delete(`festival-campaign/${id}`);
      toast.success('Deleted');
      fetchCampaigns();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleToggle = async (c) => {
    const next = c.status === 'paused' ? 'active' : 'paused';
    try {
      await ApiService.put(`festival-campaign/${c.id}/status`, { status: next });
      toast.success(`Campaign ${next === 'active' ? 'activated' : 'paused'}`);
      fetchCampaigns();
    } catch (err) {
      toast.error('Status update failed');
    }
  };

  const handleTestTrigger = async (c) => {
    const phone = window.prompt('Send test WhatsApp to:', '7974988355');
    if (!phone) return;
    setTriggeringId(c.id);
    try {
      const res = await ApiService.post(`festival-campaign/${c.id}/trigger`, { testPhone: phone.trim() });
      if (res?.success) toast.success(res.message || `Test sent to ${phone}`);
      else toast.warn(res?.message || 'Test trigger failed');
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Test trigger failed');
    } finally {
      setTriggeringId(null);
    }
  };

  const handleTriggerNow = (c) => setConfirmCampaign(c);

  const executeTrigger = async () => {
    const c = confirmCampaign;
    setConfirmCampaign(null);
    setTriggeringId(c.id);
    try {
      const res = await ApiService.post(`festival-campaign/${c.id}/trigger`, {});
      if (res?.success) toast.success(res.message || 'Messages sent to segment');
      else toast.warn(res?.message || 'No leads reached');
      fetchCampaigns();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Trigger failed');
    } finally {
      setTriggeringId(null);
    }
  };

  const displayed = filterType === 'all'
    ? campaigns
    : campaigns.filter(c => (c.actionType || 'festival_campaign') === filterType);

  const activeCount    = campaigns.filter(c => c.status === 'active').length;
  const triggeredCount = campaigns.filter(c => c.status === 'triggered').length;

  /* ─────────────────────────────────────────────────────────────────────── */
  return (
    <div className="container-fluid">

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h3 className="mb-0">WhatsApp Marketing</h3>
          <small className="text-muted">Schedule & trigger WhatsApp campaigns via TeleCRM for all leads</small>
        </div>
        <button
          className={`btn ${showForm ? 'btn-outline-secondary' : 'btn-primary'}`}
          onClick={() => { setShowForm(v => !v); setForm(EMPTY_FORM); setEditingId(null); }}
        >
          {showForm ? '✕ Cancel' : '+ New Campaign'}
        </button>
      </div>

      {/* Stat cards */}
      <div className="row mb-4">
        {[
          { label: 'Total Campaigns',    value: campaigns.length, color: '#6c5ffc', icon: 'fa-bullhorn' },
          { label: 'Active',             value: activeCount,      color: '#2bc155', icon: 'fa-check-circle' },
          { label: 'Triggered',          value: triggeredCount,   color: '#3695eb', icon: 'fa-bolt' },
          { label: 'Upcoming Festivals', value: calEvents.length, color: '#f4a02b', icon: 'fa-star' },
        ].map(s => (
          <div className="col-xl-3 col-sm-6 mb-3" key={s.label}>
            <div className="card" style={{ borderLeft: `4px solid ${s.color}` }}>
              <div className="card-body d-flex align-items-center py-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{ width: 44, height: 44, background: s.color + '22' }}>
                  <i className={`fa ${s.icon}`} style={{ color: s.color, fontSize: 18 }} />
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{s.value}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>{s.label}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter chips */}
      <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
        <span className="text-muted me-1" style={{ fontSize: 12 }}>Filter:</span>
        <button
          className={`btn btn-xs ${filterType === 'all' ? 'btn-dark' : 'btn-outline-secondary'}`}
          style={{ borderRadius: 20, fontSize: 12 }}
          onClick={() => setFilterType('all')}
        >
          All ({campaigns.length})
        </button>
        {CAMPAIGN_TYPES.map(ct => {
          const count = campaigns.filter(c => (c.actionType || 'festival_campaign') === ct.value).length;
          if (count === 0) return null;
          return (
            <button key={ct.value}
              style={{
                background: filterType === ct.value ? ct.color : 'transparent',
                color: filterType === ct.value ? '#fff' : ct.color,
                border: `1px solid ${ct.color}`,
                borderRadius: 20, fontSize: 12, padding: '2px 12px', cursor: 'pointer',
              }}
              onClick={() => setFilterType(prev => prev === ct.value ? 'all' : ct.value)}
            >
              <i className={`fa ${ct.icon} me-1`} />{ct.label} ({count})
            </button>
          );
        })}
      </div>

      <div className="row">

        {/* LEFT — Upcoming Festivals */}
        <div className="col-xl-4 mb-4">
          <div className="card h-100">
            <div className="card-header border-0 pb-0">
              <h5 className="card-title mb-0">Upcoming Festivals</h5>
              <small className="text-muted">Next 60 days · click Use → to schedule a Festival Campaign</small>
            </div>
            <div className="card-body px-3" style={{ maxHeight: 520, overflowY: 'auto' }}>
              {loadingCal && <Loader />}
              {!loadingCal && calEvents.length === 0 && (
                <p className="text-muted text-center mt-4">No upcoming festivals in next 60 days</p>
              )}
              {calEvents.map(evt => (
                <div key={evt.id} className="d-flex align-items-start justify-content-between py-2 border-bottom">
                  <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                    <div className="fw-semibold text-truncate" style={{ fontSize: 14 }}>{evt.eventName}</div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {fmtDate(evt.eventDate)}&nbsp;·&nbsp;
                      <span style={{ color: '#f4a02b' }}>{daysFromNow(evt.eventDate)}</span>
                    </div>
                    {evt.recommendedPujaName && (
                      <div style={{ fontSize: 11, color: '#888' }}>Puja: {evt.recommendedPujaName}</div>
                    )}
                  </div>
                  <button className="btn btn-xs btn-outline-primary"
                    style={{ whiteSpace: 'nowrap', fontSize: 11 }}
                    onClick={() => prefillFromEvent(evt)}>
                    Use →
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — Form + Table */}
        <div className="col-xl-8 mb-4">

          {/* Add Campaign Form */}
          {showForm && (
            <div className="card mb-4" ref={formRef}>
              <div className="card-header border-0 pb-0">
                <h5 className="card-title mb-0">{editingId ? 'Edit Campaign' : 'New WhatsApp Campaign'}</h5>
                <small className="text-muted">Pick a campaign type — TeleCRM fires the matching workflow at the scheduled time</small>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">

                    {/* Campaign Type selector */}
                    <div className="col-12">
                      <label className="form-label fw-semibold">
                        Campaign Type <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex flex-wrap gap-2">
                        {CAMPAIGN_TYPES.map(ct => (
                          <button key={ct.value} type="button"
                            onClick={() => setForm(p => ({ ...p, actionType: ct.value, campaignName: '', selectedPuja: null }))}
                            style={{
                              background: form.actionType === ct.value ? ct.color : 'transparent',
                              color: form.actionType === ct.value ? '#fff' : ct.color,
                              border: `1.5px solid ${ct.color}`,
                              borderRadius: 20, fontSize: 12, padding: '5px 14px', cursor: 'pointer',
                              fontWeight: form.actionType === ct.value ? 600 : 400,
                            }}
                          >
                            <i className={`fa ${ct.icon} me-1`} />{ct.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Campaign Name — free text for Custom, dropdown for all others */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Campaign Name <span className="text-danger">*</span>
                      </label>
                      {form.actionType === 'custom_campaign' ? (
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter campaign name..."
                          value={form.campaignName}
                          onChange={e => setForm(p => ({ ...p, campaignName: e.target.value }))}
                        />
                      ) : (
                        <Select
                          options={campaignNameOptions}
                          value={form.campaignName ? { value: form.campaignName, label: form.campaignName } : null}
                          onChange={opt => setForm(p => ({ ...p, campaignName: opt ? opt.value : '' }))}
                          isSearchable
                          placeholder={`Select ${activeType.label} name...`}
                          classNamePrefix="react-select"
                          noOptionsMessage={() => 'No options available'}
                          styles={{
                            control: b => ({ ...b, minHeight: 42 }),
                            menu:    b => ({ ...b, zIndex: 9999 }),
                          }}
                        />
                      )}
                    </div>

                    {/* Puja — shown for all campaign types */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Select Puja</label>
                      <Select
                        options={pujaOptions}
                        value={form.selectedPuja}
                        onChange={opt => setForm(p => ({ ...p, selectedPuja: opt }))}
                        isLoading={loadingPujas}
                        isSearchable isClearable
                        placeholder="Optional — search puja..."
                        classNamePrefix="react-select"
                        styles={{
                          control: b => ({ ...b, minHeight: 42 }),
                          menu:    b => ({ ...b, zIndex: 9999 }),
                        }}
                      />
                    </div>

                    {/* Segment filter — required */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Target Segment <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={segmentOptions}
                        value={form.selectedSegment}
                        onChange={opt => setForm(p => ({ ...p, selectedSegment: opt }))}
                        isSearchable
                        placeholder="Select a segment..."
                        classNamePrefix="react-select"
                        styles={{
                          control: b => ({ ...b, minHeight: 42, borderColor: !form.selectedSegment ? '#f4a02b' : b.borderColor }),
                          menu:    b => ({ ...b, zIndex: 9999 }),
                        }}
                      />
                      <small className="text-muted">Messages will only be sent to users in the selected segment.</small>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Date (IST) <span className="text-danger">*</span></label>
                      <input type="date" className="form-control"
                        min={new Date().toISOString().split('T')[0]}
                        value={form.scheduledDate}
                        onChange={e => setForm(p => ({ ...p, scheduledDate: e.target.value }))}
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Time (IST) <span className="text-danger">*</span></label>
                      <input type="time" className="form-control"
                        value={form.scheduledTime}
                        onChange={e => setForm(p => ({ ...p, scheduledTime: e.target.value }))}
                      />
                      <small className="text-muted">Cron fires every minute</small>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Notes</label>
                      <input type="text" className="form-control" placeholder="Optional"
                        value={form.notes}
                        onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                      />
                    </div>

                    {/* Daily Message Limit */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Daily Message Limit</label>
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {DAILY_LIMIT_PRESETS.map(preset => (
                          <button key={preset.value} type="button"
                            onClick={() => setForm(p => ({ ...p, dailyLimit: preset.value, dailyLimitCustom: '' }))}
                            style={{
                              background: form.dailyLimit === preset.value ? '#6c5ffc' : 'transparent',
                              color: form.dailyLimit === preset.value ? '#fff' : '#6c5ffc',
                              border: '1.5px solid #6c5ffc',
                              borderRadius: 20, fontSize: 12, padding: '5px 14px', cursor: 'pointer',
                              fontWeight: form.dailyLimit === preset.value ? 600 : 400,
                            }}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                      {form.dailyLimit === 'custom' && (
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Enter message count..."
                          min={1}
                          value={form.dailyLimitCustom}
                          onChange={e => setForm(p => ({ ...p, dailyLimitCustom: e.target.value }))}
                        />
                      )}
                      <small className="text-muted">Max messages sent per campaign trigger</small>
                    </div>

                  </div>

                  {/* Preview */}
                  {form.campaignName && form.scheduledDate && form.selectedSegment?.value && (
                    <div className="alert alert-info mt-3 mb-0 py-2" style={{ fontSize: 13 }}>
                      <i className={`fa ${activeType.icon} me-1`} style={{ color: activeType.color }} />
                      <strong>{form.campaignName}</strong>
                      {form.selectedPuja && <> · <em>{form.selectedPuja.label}</em></>}
                      {' '}will fire on <strong>{fmtDate(form.scheduledDate)}</strong> at{' '}
                      <strong>{form.scheduledTime} IST</strong> for{' '}
                      <strong>{form.selectedSegment.label}</strong> segment users.
                    </div>
                  )}

                  <div className="mt-3 d-flex gap-2">
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? 'Saving…' : editingId ? 'Update Campaign' : 'Save Campaign'}
                    </button>
                    <button type="button" className="btn btn-outline-secondary"
                      onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setEditingId(null); }}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Campaigns Table */}
          <div className="card">
            <div className="card-header border-0 d-flex justify-content-between align-items-center">
              <div>
                <h5 className="card-title mb-0">Campaigns</h5>
                <small className="text-muted">Cron fires TeleCRM WhatsApp at scheduled IST date &amp; time</small>
              </div>
              {!showForm && (
                <button className="btn btn-sm btn-primary" onClick={() => setShowForm(true)}>+ Add</button>
              )}
            </div>
            <div className="card-body p-0">
              {loadingCampaigns && <Loader />}
              <div className="table-responsive">
                <table className="table mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 30 }}>#</th>
                      <th>Type</th>
                      <th>Campaign</th>
                      <th>Segment</th>
                      <th>Scheduled (IST)</th>
                      <th>Status</th>
                      <th>Msgs Sent / Remaining</th>
                      <th>Last Triggered</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayed.length === 0 && !loadingCampaigns ? (
                      <tr>
                        <td colSpan="9" className="text-center py-5 text-muted">
                          No campaigns yet.{' '}
                          <button className="btn btn-link p-0" onClick={() => setShowForm(true)}>Add one</button>
                        </td>
                      </tr>
                    ) : (
                      displayed.map((c, i) => {
                        const ct = TYPE_MAP[c.actionType] || TYPE_MAP['custom_campaign'];
                        return (
                          <tr key={c.id}>
                            <td><strong>{i + 1}</strong></td>
                            <td>
                              <span style={{
                                background: ct.color + '20',
                                color: ct.color,
                                border: `1px solid ${ct.color}`,
                                borderRadius: 12, fontSize: 11, padding: '2px 8px', whiteSpace: 'nowrap',
                              }}>
                                <i className={`fa ${ct.icon} me-1`} />{ct.label}
                              </span>
                            </td>
                            <td>
                              <div className="fw-semibold">{c.festivalName}</div>
                              {c.pujaName && c.pujaName !== c.festivalName && (
                                <div style={{ fontSize: 11, color: '#888' }}>{c.pujaName}</div>
                              )}
                            </td>
                            <td>
                              {c.segment
                                ? <span className="badge badge-info light" style={{ fontSize: 11 }}>{c.segment.name}</span>
                                : <span className="text-muted" style={{ fontSize: 11 }}>All Leads</span>
                              }
                            </td>
                            <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                              {fmtDate(c.scheduledDate)}<br />
                              <span className="text-muted">{c.scheduledTime} IST</span>
                            </td>
                            <td>
                              <span className={`badge light ${STATUS_META[c.status]?.cls || 'badge-secondary'}`}>
                                {STATUS_META[c.status]?.label || c.status}
                              </span>
                            </td>
                            <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                              {c.totalTargets > 0 ? (
                                <>
                                  <span style={{ color: '#2bc155', fontWeight: 600 }}>
                                    {c.sentCount}
                                  </span>
                                  <span className="text-muted"> / </span>
                                  <span style={{ color: '#e83e8c', fontWeight: 600 }}>
                                    {Math.max(0, c.totalTargets - c.sentCount)}
                                  </span>
                                  <div style={{ fontSize: 10, color: '#aaa' }}>of {c.totalTargets} total</div>
                                </>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>
                              {c.lastTriggeredAt ? new Date(c.lastTriggeredAt).toLocaleString('en-IN') : '—'}
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <button className="btn btn-info shadow btn-xs sharp me-1"
                                title="Test on my phone" disabled={triggeringId === c.id}
                                onClick={() => handleTestTrigger(c)}>
                                {triggeringId === c.id ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-flask" />}
                              </button>
                              <button className="btn btn-success shadow btn-xs sharp me-1"
                                title="Send to segment leads" disabled={triggeringId === c.id}
                                onClick={() => handleTriggerNow(c)}>
                                {triggeringId === c.id ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-bolt" />}
                              </button>
                              <button className="btn btn-primary shadow btn-xs sharp me-1"
                                title="Edit campaign"
                                onClick={() => handleEdit(c)}>
                                <i className="fa fa-pencil" />
                              </button>
                              {c.status !== 'triggered' && (
                                <button
                                  className={`btn shadow btn-xs sharp me-1 ${c.status === 'paused' ? 'btn-info' : 'btn-warning'}`}
                                  title={c.status === 'paused' ? 'Activate' : 'Pause'}
                                  onClick={() => handleToggle(c)}>
                                  <i className={`fa ${c.status === 'paused' ? 'fa-play' : 'fa-pause'}`} />
                                </button>
                              )}
                              <button className="btn btn-danger shadow btn-xs sharp"
                                title="Delete" onClick={() => handleDelete(c.id, c.festivalName)}>
                                <i className="fa fa-trash" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Trigger-now confirmation modal */}
      {confirmCampaign && (
        <>
          <div
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
              zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={() => setConfirmCampaign(null)}
          >
            <div
              style={{
                background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460,
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)', padding: 0, overflow: 'hidden',
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal header */}
              <div style={{ background: '#6c5ffc', padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className="fa fa-bolt" style={{ color: '#fff', fontSize: 18 }} />
                </div>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Trigger Campaign Now?</div>
                  <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>This will send messages immediately</div>
                </div>
              </div>

              {/* Modal body */}
              <div style={{ padding: '20px 24px' }}>
                <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Campaign</span>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#222' }}>{confirmCampaign.festivalName}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    <div>
                      <span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Scheduled for</span>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#444' }}>
                        {fmtDate(confirmCampaign.scheduledDate)} &nbsp;·&nbsp; {confirmCampaign.scheduledTime} IST
                      </div>
                    </div>
                    <div>
                      <span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Segment</span>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#444' }}>
                        {confirmCampaign.segment?.name || '—'}
                      </div>
                    </div>
                    <div>
                      <span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Msg Limit</span>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#444' }}>
                        {confirmCampaign.dailyMessageLimit ?? 'Unlimited'}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{
                  background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8,
                  padding: '10px 14px', fontSize: 13, color: '#7a5c00', display: 'flex', gap: 8,
                }}>
                  <i className="fa fa-exclamation-triangle" style={{ marginTop: 2, flexShrink: 0 }} />
                  <span>
                    This campaign is scheduled for <strong>{fmtDate(confirmCampaign.scheduledDate)}</strong>.
                    Do you want to trigger it <strong>now</strong> instead?
                  </span>
                </div>
              </div>

              {/* Modal footer */}
              <div style={{ padding: '12px 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-outline-secondary"
                  style={{ minWidth: 100 }}
                  onClick={() => setConfirmCampaign(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success"
                  style={{ minWidth: 140 }}
                  onClick={executeTrigger}
                >
                  <i className="fa fa-bolt me-1" /> Yes, Send Now
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

